// src/pages/DailyData.jsx
import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../utils/api';

// ── Symbol typeahead (same pattern as Charts.jsx) ─────────────────────────────

function SymbolSearch({ value, onChange, onSelect }) {
  const [results,   setResults]   = useState([]);
  const [open,      setOpen]      = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounce = useRef(null);
  const wrapRef  = useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    clearTimeout(debounce.current);
    if (!value || value.length < 1) { setResults([]); return; }
    debounce.current = setTimeout(() => {
      authFetch(`/api/instruments/search?q=${encodeURIComponent(value)}&limit=12`)
        .then(r => r.json())
        .then(d => { setResults(d.symbols || []); setActiveIdx(-1); })
        .catch(() => setResults([]));
    }, 180);
  }, [value]);

  function pick(item) { onSelect(item.symbol); setResults([]); setOpen(false); }

  function onKey(e) {
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && activeIdx >= 0) pick(results[activeIdx]);
    if (e.key === 'Escape') setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', minWidth: 200 }}>
      <input
        value={value}
        onChange={e => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder="Type symbol (e.g. RELIANCE)…"
        autoComplete="off"
        spellCheck={false}
        style={{
          width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.95rem',
          fontWeight: 600, fontFamily: 'monospace',
          border: '1px solid #cbd5e1', borderRadius: 7, outline: 'none',
        }}
      />
      {open && results.length > 0 && (
        <ul style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 999,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', listStyle: 'none',
          margin: '2px 0 0', padding: 0, minWidth: 260, maxHeight: 300, overflowY: 'auto',
        }}>
          {results.map((r, i) => (
            <li
              key={r.symbol}
              onMouseDown={() => pick(r)}
              style={{
                padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.87rem',
                background: i === activeIdx ? '#eff6ff' : 'transparent',
                display: 'flex', justifyContent: 'space-between', gap: '0.5rem',
              }}
            >
              <span style={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.symbol}</span>
              <span style={{ color: '#64748b', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.name}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function DailyData() {
  const [inputVal, setInputVal] = useState('');
  const [symbol,   setSymbol]   = useState('');
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  // Load data when symbol changes
  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    authFetch(`/api/daily-data?symbol=${encodeURIComponent(symbol)}&limit=500`)
      .then(r => r.json())
      .then(d => { setRows(d.data || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [symbol]);

  function handleSelect(sym) {
    setInputVal(sym);
    setSymbol(sym);
  }

  const fmt = (n, d = 2) => n != null
    ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d })
    : '—';

  return (
    <div style={{ padding: '1rem', maxWidth: 1000 }}>
      <h1 style={{ margin: '0 0 0.25rem', fontSize: '1.35rem', fontWeight: 800, color: '#e2e8f0' }}>
        Daily Data
      </h1>
      <p style={{ margin: '0 0 1rem', fontSize: '0.78rem', color: '#64748b' }}>
        Historical daily OHLCV from your local database
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <SymbolSearch value={inputVal} onChange={setInputVal} onSelect={handleSelect} />
        {loading && <span style={{ color: '#64748b', fontSize: '0.82rem' }}>Loading…</span>}
        {rows.length > 0 && !loading && (
          <span style={{ color: '#64748b', fontSize: '0.82rem' }}>{rows.length} rows</span>
        )}
      </div>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 7, padding: '0.7rem 1rem', color: '#991b1b', marginBottom: '0.75rem' }}>
          {error}
        </div>
      )}

      {!symbol && !loading && (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
          Type a symbol above to load its daily OHLCV history.
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto', maxHeight: '75vh', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead style={{ position: 'sticky', top: 0 }}>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={th}>Date</th>
                  <th style={{ ...th, textAlign: 'right' }}>Open</th>
                  <th style={{ ...th, textAlign: 'right' }}>High</th>
                  <th style={{ ...th, textAlign: 'right' }}>Low</th>
                  <th style={{ ...th, textAlign: 'right' }}>Close</th>
                  <th style={{ ...th, textAlign: 'right' }}>Volume</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const up = r.close >= r.open;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ ...td, fontWeight: 600, color: '#0f172a' }}>{String(r.date)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#475569' }}>{fmt(r.open)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#16a34a' }}>{fmt(r.high)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#dc2626' }}>{fmt(r.low)}</td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: up ? '#16a34a' : '#dc2626' }}>{fmt(r.close)}</td>
                      <td style={{ ...td, textAlign: 'right', color: '#64748b' }}>{Number(r.volume).toLocaleString('en-IN')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && rows.length === 0 && symbol && !error && (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '2rem' }}>
          No data for <strong>{symbol}</strong>. Use Settings → Data Fetch to load historical data.
        </p>
      )}
    </div>
  );
}

const th = { padding: '0.55rem 0.75rem', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left' };
const td = { padding: '0.45rem 0.75rem' };
